import type {
  LDClient,
  LDEvaluationDetail,
  LDFlagSet,
  LDFlagValue,
  LDUser,
  NonNullableLDEvaluationReason,
} from 'launchdarkly-js-client-sdk';

/**
 * FakeLDClient is only for preventing networking connections from being made in
 * local development. For testing, use the {@link jest-launchdarkly-mock} package.
 */
export class FakeLaunchDarklyClient implements LDClient {
  flags: LDFlagSet = {};

  user: LDUser;

  constructor(flags: LDFlagSet, ldUser: LDUser) {
    this.flags = flags;
    this.user = ldUser;
  }

  // eslint-disable-next-line class-methods-use-this
  waitUntilGoalsReady(): Promise<void> {
    return Promise.resolve();
  }

  // eslint-disable-next-line class-methods-use-this
  waitUntilReady(): Promise<void> {
    return Promise.resolve();
  }

  // eslint-disable-next-line class-methods-use-this
  waitForInitialization(): Promise<void> {
    return Promise.resolve();
  }

  identify(
    user: LDUser,
    _hash?: string | undefined,
    onDone?: (err: Error | null, flags: LDFlagSet | null) => void
  ): Promise<LDFlagSet> {
    this.user = user;
    if (onDone) {
      // This branch is supposed to return undefined as it does in
      // LaunchDarkly's JS code, but the return type of `identify` they've
      // provided doesn't allow that.
      onDone(null, this.flags);
    }
    return Promise.resolve(this.flags);
  }

  getUser(): LDUser {
    return this.user;
  }

  // eslint-disable-next-line class-methods-use-this
  flush(onDone?: (() => void) | undefined): Promise<void> {
    onDone?.();
    return Promise.resolve();
  }

  variation(key: string, defaultValue?: LDFlagValue): LDFlagValue {
    return this.flags[key] || defaultValue;
  }

  variationDetail(key: string, defaultValue?: LDFlagValue): LDEvaluationDetail {
    const reason: NonNullableLDEvaluationReason = {
      kind: 'FALLTHROUGH',
    };
    const detail: LDEvaluationDetail = {
      value: this.flags[key] || defaultValue,
      variationIndex: 0,
      reason,
    };
    return detail;
  }

  // eslint-disable-next-line class-methods-use-this
  setStreaming(/* value?: boolean | undefined */): void {}

  // eslint-disable-next-line class-methods-use-this
  on(/* key: string, callback: (...args: any[]) => void, context?: any */): void {}

  // eslint-disable-next-line class-methods-use-this
  off(/* key: string, callback: (...args: any[]) => void, context?: any */): void {}

  // eslint-disable-next-line class-methods-use-this
  track(/* key: string, data?: any, metricValue?: number | undefined */): void {}

  // eslint-disable-next-line class-methods-use-this
  alias(/* user: LDUser, previousUser: LDUser */): void {}

  allFlags(): LDFlagSet {
    return this.flags;
  }

  // eslint-disable-next-line class-methods-use-this
  close(onDone?: (() => void) | undefined): Promise<void> {
    onDone?.();
    return Promise.resolve();
  }
}
